import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { validateAppointmentToken } from '../../firebase/appointments';
import { formatDate } from '../../utils/dateUtils';
import { doc, updateDoc, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase/firebase';

/**
 * Página para cliente cancelar agendamento através do link enviado por WhatsApp
 * @returns {JSX.Element} Componente React
 */
const CancelPage = () => {
  const { appointmentId } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  // Estados
  const [status, setStatus] = useState('loading'); // loading, valid, invalid, canceled, error
  const [appointment, setAppointment] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [cancelReason, setCancelReason] = useState('');
  
  // Validar token e carregar agendamento
  useEffect(() => {
    const validateAndLoad = async () => {
      try {
        if (!appointmentId || !token) {
          setStatus('invalid');
          setErrorMessage('Link inválido ou expirado. Por favor, entre em contato com o estabelecimento.');
          return;
        }
        
        const appointmentData = await validateAppointmentToken(appointmentId, token);
        
        if (!appointmentData) {
          setStatus('invalid');
          setErrorMessage('Link inválido ou expirado. Por favor, entre em contato com o estabelecimento.');
          return;
        }
        
        setAppointment(appointmentData);
        
        // Verificar se já está cancelado
        if (appointmentData.cancelado) {
          setStatus('canceled');
        } else {
          setStatus('valid');
        }
      } catch (error) {
        console.error('Erro ao validar token:', error);
        setStatus('error');
        setErrorMessage('Ocorreu um erro ao processar sua solicitação. Por favor, tente novamente ou entre em contato com o estabelecimento.');
      }
    };
    
    validateAndLoad();
  }, [appointmentId, token]);
  
  // Cancelar agendamento
  const handleCancel = async () => {
    try {
      setStatus('loading');
      
      // Atualizar status no Firebase
      const appointmentRef = doc(db, `salons/${appointmentId.split('/')[0]}/appointments/${appointmentId}`);
      await updateDoc(appointmentRef, {
        cancelado: true,
        confirmado: false,
        motivoCancelamento: cancelReason || 'Cancelado pelo cliente',
        dataCancelamento: Timestamp.now()
      });
      
      setStatus('canceled');
    } catch (error) {
      console.error('Erro ao cancelar agendamento:', error);
      setStatus('error');
      setErrorMessage('Não foi possível cancelar seu agendamento. Por favor, entre em contato com o estabelecimento.');
    }
  };
  
  // Renderizar mensagem de carregamento
  if (status === 'loading') {
    return (
      <div className="flex min-h-screen bg-gray-100 justify-center items-center p-4">
        <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Carregando informações do agendamento...</p>
        </div>
      </div>
    );
  }
  
  // Renderizar erro ou link inválido
  if (status === 'invalid' || status === 'error') {
    return (
      <div className="flex min-h-screen bg-gray-100 justify-center items-center p-4">
        <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full">
          <div className="flex justify-center mb-4">
            <div className="bg-red-100 rounded-full p-3">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
          </div>
          <h2 className="text-2xl font-bold text-center text-gray-800 mb-4">
            Não foi possível processar sua solicitação
          </h2>
          <p className="text-gray-600 mb-6 text-center">
            {errorMessage}
          </p>
          <div className="text-center">
            <a href="/" className="inline-block bg-blue-500 hover:bg-blue-600 text-white font-medium py-2 px-4 rounded transition-colors">
              Voltar para página inicial
            </a>
          </div>
        </div>
      </div>
    );
  }
  
  // Renderizar agendamento já cancelado
  if (status === 'canceled') {
    return (
      <div className="flex min-h-screen bg-gray-100 justify-center items-center p-4">
        <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full">
          <div className="flex justify-center mb-4">
            <div className="bg-red-100 rounded-full p-3">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
          </div>
          <h2 className="text-2xl font-bold text-center text-gray-800 mb-4">
            Agendamento Cancelado
          </h2>
          <div className="border-b border-gray-200 pb-4 mb-4">
            <p className="text-gray-600 text-center">
              Seu agendamento foi cancelado com sucesso.
            </p>
          </div>
          
          {appointment && (
            <div className="bg-gray-50 p-4 rounded-lg mb-6">
              <p className="font-medium text-gray-800">
                {appointment.cliente || 'Cliente'}
              </p>
              <p className="text-gray-600">
                <span className="font-medium">Data:</span> {formatDate(appointment.data)}
              </p>
              <p className="text-gray-600">
                <span className="font-medium">Horário:</span> {appointment.horario?.substring(0, 5) || 'Não informado'}
              </p>
              <p className="text-gray-600">
                <span className="font-medium">Serviço:</span> {
                  Array.isArray(appointment.servicos) && appointment.servicos
                    .map(s => typeof s === 'object' && s.nome ? s.nome : 'Serviço')
                    .join(', ')
                }
              </p>
              <p className="text-gray-600">
                <span className="font-medium">Profissional:</span> {
                  typeof appointment.profissional === 'object' 
                    ? appointment.profissional.nome 
                    : appointment.profissional || 'Não informado'
                }
              </p>
            </div>
          )}
          
          <div className="text-center">
            <a href="/" className="inline-block bg-blue-500 hover:bg-blue-600 text-white font-medium py-2 px-4 rounded transition-colors">
              Voltar para página inicial
            </a>
          </div>
        </div>
      </div>
    );
  }
  
  // Renderizar confirmação de cancelamento
  return (
    <div className="flex min-h-screen bg-gray-100 justify-center items-center p-4">
      <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full">
        <h2 className="text-2xl font-bold text-center text-gray-800 mb-4">
          Cancelar Agendamento
        </h2>
        
        {appointment && (
          <div className="mb-6">
            <div className="bg-gray-50 p-4 rounded-lg mb-4">
              <p className="font-medium text-gray-800">
                {appointment.cliente || 'Cliente'}
              </p>
              <p className="text-gray-600">
                <span className="font-medium">Data:</span> {formatDate(appointment.data)}
              </p>
              <p className="text-gray-600">
                <span className="font-medium">Horário:</span> {appointment.horario?.substring(0, 5) || 'Não informado'}
              </p>
              <p className="text-gray-600">
                <span className="font-medium">Serviço:</span> {
                  Array.isArray(appointment.servicos) && appointment.servicos
                    .map(s => typeof s === 'object' && s.nome ? s.nome : 'Serviço')
                    .join(', ')
                }
              </p>
              <p className="text-gray-600">
                <span className="font-medium">Profissional:</span> {
                  typeof appointment.profissional === 'object' 
                    ? appointment.profissional.nome 
                    : appointment.profissional || 'Não informado'
                }
              </p>
            </div>
            
            <div className="bg-red-50 border border-red-100 rounded-md p-4 mb-6">
              <div className="flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-red-400 mr-2" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <p className="text-red-700 text-sm font-medium">
                  Tem certeza que deseja cancelar este agendamento?
                </p>
              </div>
            </div>
            
            <div className="mb-4">
              <label htmlFor="reason" className="block text-sm font-medium text-gray-700 mb-1">
                Motivo do cancelamento (opcional)
              </label>
              <textarea
                id="reason"
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500"
                rows="3"
                placeholder="Informe o motivo do cancelamento..."
              ></textarea>
            </div>
            
            <div className="flex flex-col sm:flex-row sm:justify-center space-y-3 sm:space-y-0 sm:space-x-3">
              <button
                onClick={() => window.history.back()}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
              >
                Voltar
              </button>
              <button
                onClick={handleCancel}
                className="px-4 py-2 border border-transparent rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                Confirmar Cancelamento
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CancelPage; 